import { useContext } from 'react';
import { Box } from '@givebutter/primitives/box';
import { Label } from '@givebutter/primitives/label';
import { faTicket } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FilterBarContainer } from 'components/filter/filters-next/FilterBarContainer';
import { FilterGroupBar } from 'components/filter/filters-next/FilterGroupBar';
import { SaveNewSegmentButton } from 'components/filter/filters-next/SaveNewSegmentButton';
import { SegmentsIndexDrawer } from 'components/filter/filters-next/SegmentIndexDrawer';
import * as Input from 'components/input';
import { TicketTableKey, transformTableKeyToReadable } from 'components/pages/tickets/transform';
import * as Table from 'components/table';
import { TableContext } from 'components/table/TableContext';
import { TableCoreStateContext } from 'components/table/TableCoreStateContext';
import { ResourceTableProps } from 'components/table/types';
import { useTableSchema } from 'customHooks/useTableSchema';

interface EventAttendeesTableProps extends ResourceTableProps {}

const EventAttendeesTable = (props: EventAttendeesTableProps) => {
    const { children, enableFilters = true, enableReset = true } = props;
    const { enableSchema, search, setSearch } = useContext(TableCoreStateContext);
    const {
        data: { columns: columnSchema },
    } = useTableSchema('tickets');
    const { table } = useContext(TableContext);

    return (
        <Box className="flex flex-col">
            <div className={'flex flex-col'}>
                <div className={'flex justify-between gap-4 border-b border-b-gray-100 p-4'}>
                    <div className={'flex flex-wrap gap-1.5'}>
                        <Label className="sr-only" htmlFor="campaign_tickets_search">
                            Search
                        </Label>
                        <Input.Root size={'sm'}>
                            <Input.SearchIcon />
                            <Input.Form
                                id="campaign_tickets_search"
                                value={search}
                                placeholder={'Search'}
                                onChange={e => setSearch?.(e.target.value)}
                            />
                        </Input.Root>
                    </div>
                    <div className={'flex items-center gap-1'}>
                        <Table.ResultsCount />
                        {enableFilters && <SaveNewSegmentButton />}
                        {enableFilters && <SegmentsIndexDrawer />}
                        {table.options.enableHiding && (
                            <Table.ColumnVisibility
                                transformIdToReadable={id => {
                                    const calculatedColumn = columnSchema.find(
                                        col => col.name === id,
                                    );
                                    if (enableSchema && calculatedColumn) {
                                        return calculatedColumn.label;
                                    }

                                    return transformTableKeyToReadable(id as TicketTableKey);
                                }}
                            />
                        )}
                        <Table.ToggleFullWidth />
                        {enableReset && <Table.Reset />}
                    </div>
                </div>
                {enableFilters && (
                    <FilterBarContainer>
                        <FilterGroupBar
                            quickFilters={[
                                'created_at',
                                'status',
                                'custom_fields',
                                'ticketBundles',
                            ]}
                        />
                    </FilterBarContainer>
                )}
            </div>
            <Table.FiltersBar />
            <Table.Filters />
            <Table.Content>
                <Table.Header />
                <Table.Body />
            </Table.Content>
            <Table.Empty variant={'primary'}>
                <Table.EmptyIcon>
                    <FontAwesomeIcon icon={faTicket} />
                </Table.EmptyIcon>
                <Table.EmptyTitle>No attendees found</Table.EmptyTitle>
                <Table.EmptyWhenNotSearching>{children}</Table.EmptyWhenNotSearching>
            </Table.Empty>
            <Table.Pagination className={'border-t border-t-gray-100 p-2'}>
                <Table.PaginationPageNavigation />
                <Table.PaginationPerPageDropdown />
            </Table.Pagination>
        </Box>
    );
};

export { EventAttendeesTable };
